"use client";

import { HINT, LABEL } from "@/components/tool-kit/styles";

export interface T9Word {
  code: string;
  candidates: string[];
  chosen: number;
}

export interface T9PickerLabels {
  heading: string;
  hint: string;
  noMatch: string;
  /** "Word {n}" for a group's accessible name. */
  wordLabel: (n: number) => string;
}

/**
 * One row per typed word: the digits, then the words they could be, commonest first, as
 * buttons. The chosen one is marked; picking another changes the decoded text.
 */
export function T9Picker({
  words,
  labels,
  onChoose,
}: {
  words: T9Word[];
  labels: T9PickerLabels;
  onChoose: (wordIndex: number, candidateIndex: number) => void;
}) {
  if (words.length === 0) {
    return null;
  }
  return (
    <section className="space-y-3">
      <h3 className={LABEL}>{labels.heading}</h3>
      <p className={HINT}>{labels.hint}</p>
      <ul className="space-y-2">
        {words.map((word, wordIndex) => (
          <li
            className="flex flex-wrap items-center gap-2"
            // The words are positions in the typed text.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional list
            key={wordIndex}
          >
            <span className="w-24 shrink-0 font-mono text-muted-foreground text-xs">
              {word.code}
            </span>
            {word.candidates.length === 0 ? (
              <span className={HINT}>{labels.noMatch}</span>
            ) : (
              <fieldset
                aria-label={labels.wordLabel(wordIndex + 1)}
                className="flex flex-wrap gap-2"
              >
                {word.candidates.map((candidate, candidateIndex) => (
                  <button
                    aria-pressed={candidateIndex === word.chosen}
                    className={`rounded-md border px-2 py-1 text-sm ${
                      candidateIndex === word.chosen
                        ? "border-primary bg-primary/10 font-medium text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                    key={candidate}
                    onClick={() => onChoose(wordIndex, candidateIndex)}
                    type="button"
                  >
                    {candidate}
                  </button>
                ))}
              </fieldset>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
